import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, Check, MessageCircle } from "lucide-react";
import { findProduct } from "@/data/products";
import { fetchProducts, imgUrl } from "@/lib/api";
import { useTitle, useJsonLd } from "@/lib/useTitle";
import { useEnquiry } from "@/context/EnquiryContext";
import QuoteModal from "@/components/QuoteModal";
import { wa } from "@/lib/brand";
import { toast } from "sonner";

const Spec = ({ label, value }) => (
  <div className="border-b border-[#004225]/10 py-3 flex justify-between gap-6 text-sm">
    <span className="text-[#171717]/60">{label}</span>
    <span className="text-[#171717] font-medium text-right">{value}</span>
  </div>
);

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(undefined);
  const [related, setRelated] = useState([]);
  const { addItem, hasProduct } = useEnquiry();
  const [modal, setModal] = useState(false);

  useEffect(() => {
    fetchProducts().then((list) => {
      const p = list.find((x) => x.id === id) || null;
      setProduct(p);
      if (p) setRelated(list.filter((x) => x.category === p.category && x.id !== p.id).slice(0, 3));
    });
  }, [id]);

  useTitle(
    product ? `${product.name} — Bulk Export Supplier India | GN Agro Traders` : "Product — GN Agro Traders",
    product ? `${product.name}: export-grade Indian ${product.variety} (${product.type}). ${product.packaging}. MOQ ${product.moq}. Request bulk quotation from GN Agro Traders.` : undefined
  );
  useJsonLd("product-ld", product ? {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    image: product.image,
    description: product.description || `${product.name} — export-grade ${product.variety}, ${product.type}, origin ${product.origin}.`,
    brand: { "@type": "Brand", name: "GN Agro Traders" },
    category: product.category,
    url: `https://gnagrotraders.com/product/${product.id}`,
  } : null);

  if (product === undefined) {
    return <div className="max-w-3xl mx-auto p-16 text-center text-[#171717]/60">Loading product…</div>;
  }
  if (!product) {
    return (
      <div className="max-w-3xl mx-auto p-16 text-center">
        <h1 className="font-display text-3xl text-[#004225]">Product not found</h1>
        <Link to="/products" className="mt-4 inline-block text-[#004225] font-semibold">← Back to Products</Link>
      </div>
    );
  }
  const added = hasProduct(product.id);

  return (
    <div>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <nav className="text-[11px] font-eyebrow text-[#171717]/50 mb-3" aria-label="Breadcrumb" data-testid="breadcrumbs">
          <Link to="/" className="hover:text-[#004225]">Home</Link>
          <span className="mx-2 text-[#D4AF37]">/</span>
          <Link to="/products" className="hover:text-[#004225]">Products</Link>
          <span className="mx-2 text-[#D4AF37]">/</span>
          <Link to={`/products/${product.category}`} className="hover:text-[#004225]">{product.category.replace(/-/g, " ")}</Link>
          <span className="mx-2 text-[#D4AF37]">/</span>
          <span className="text-[#004225]">{product.name}</span>
        </nav>
        <Link to="/products" className="inline-flex items-center gap-1 text-[13px] text-[#004225]/80 hover:text-[#004225]"><ArrowLeft className="h-4 w-4" />Back to Products</Link>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-2 gap-10">
        <div className="bg-white rounded-sm border border-[#004225]/10 overflow-hidden">
          <img src={imgUrl(product.image)} alt={product.name} className="w-full h-full object-cover aspect-[4/3]" />
        </div>
        <div>
          <div className="text-[11px] font-eyebrow text-[#B5942D]">{product.type}</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl text-[#004225] leading-tight">{product.name}</h1>
          <p className="mt-3 text-[14px] text-[#171717]/75 leading-relaxed">
            {product.description || `Export-grade ${product.name} sourced through GN Agro Traders' Indian sourcing network. Specifications, packaging and volumes are flexible to buyer requirements.`}
          </p>

          <div className="mt-6 bg-white rounded-sm border border-[#004225]/10 p-5">
            <div className="text-[11px] font-eyebrow text-[#B5942D]">Specifications</div>
            <div className="mt-2">
              <Spec label="Variety" value={product.variety} />
              <Spec label="Type / Processing" value={product.type} />
              <Spec label="Origin" value={product.origin} />
              <Spec label="Grain / Quality" value={product.grain} />
              <Spec label="Moisture" value={product.moisture} />
              <Spec label="Broken" value={product.broken} />
              <Spec label="MOQ" value={product.moq} />
              <Spec label="Packaging" value={product.packaging} />
              <Spec label="Incoterms" value="FOB / CFR / CIF" />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <button
              onClick={() => { if (added) { toast.info("Already in enquiry"); return; } addItem(product); toast.success("Added to enquiry"); }}
              data-testid={`detail-add-${product.id}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest ${added ? "bg-[#F7F2E2] text-[#004225]" : "btn-primary"}`}
            >
              {added ? (<><Check className="h-4 w-4" />In Enquiry</>) : (<><Plus className="h-4 w-4" />Add to Enquiry</>)}
            </button>
            <button onClick={() => setModal(true)} data-testid="detail-request-quote" className="inline-flex items-center gap-2 rounded-full btn-gold px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest">Request Bulk Quote</button>
            <a href={wa(`Hello, I'm interested in ${product.name}. Please share specifications and quote.`)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </div>
          <div className="mt-6 text-[12px] text-[#171717]/60">Pricing is not published publicly — request current pricing based on specification, quantity, packaging, destination and market conditions.</div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h3 className="font-display text-2xl text-[#004225]">Related Products</h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {related.map((p) => (
              <Link key={p.id} to={`/product/${p.id}`} className="block bg-white rounded-sm border border-[#004225]/10 overflow-hidden card-elevate">
                <img src={imgUrl(p.image)} alt={p.name} className="aspect-[4/3] object-cover w-full" />
                <div className="p-4">
                  <div className="text-[10px] font-eyebrow text-[#B5942D]">{p.variety}</div>
                  <div className="mt-1 font-display text-lg text-[#004225]">{p.name}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <QuoteModal open={modal} onOpenChange={setModal} product={product} source="product-page" />
    </div>
  );
};

export default ProductDetail;
